import {Cell, Column} from '@blueprintjs/table';
import {Button, Input, List} from 'antd';
import BlueprintTableInfiniteScroller from 'components/BlueprintTableInfiniteScroller';
import DeleteEntity from 'Mutations/DeleteEntity';
import React, {useContext, useRef} from 'react';
import {
    DuplicationChecker,
    MDDetails,
    MDHeader,
    MDLayout,
    MDTable,
    TableLoader,
    useMasterDataState
} from 'components/masterdata-2-0';
import ComplianceRuleEntryForm from './entry-form';
import {
    getMDTableProps,
    MasterDataProps,
    MDStateToDetailsProps,
    MDStateToHeaderProps,
    MDStateToLayoutProps,
    MDStateToTableScrollerProps,
    renderCellFromKeyIndex,
    renderNoData,
    trimString
} from 'components/masterdata-2-0/util';
import {OrgDataContext} from "../../../../context/orgData";
import {
    ComplianceRuleFragmentFragment,
    CustomerComplianceRulesSearchInput,
    MangoQueryResult,
    QueryCustomerComplianceRulesArgs
} from "../../../../__generated__/graphql/types";
import {ComplianceRuleEntryFormData} from "./types";
import {useApolloClient} from "react-apollo";
import {momentOrNull} from "../../../../common/util";
import {MUTATION_SET_COMPLIANCE_RULE, QUERY_CUSTOMER_COMPLIANCE_RULES, MUTATION_DELETE_COMPLIANCE_RULE} from "./graphql";


type QueryResult = {
    CustomerComplianceRules: Omit<MangoQueryResult, 'docs'> & { docs: ComplianceRuleFragmentFragment[] }
}

export interface ComplianceRulesPageProps extends MasterDataProps {}

const DELETE_USER_GROUPS = ['flytsuite.compliance.rule.all'];

const ComplianceRulesPage: React.FC<ComplianceRulesPageProps> = (props) => {
    const formRef = useRef(null);
    const orgData = useContext(OrgDataContext);

    const baseQueryArgs: QueryCustomerComplianceRulesArgs = {}

    const MDState = useMasterDataState({
        getQueryData: (data: QueryResult) => data.CustomerComplianceRules.docs,
        getQueryVariables: (searchValues: CustomerComplianceRulesSearchInput) => ({
            ...baseQueryArgs,
            search: {
                ...searchValues
            }
        }),
        queryGQL: QUERY_CUSTOMER_COMPLIANCE_RULES,
        saveMutationGQL: MUTATION_SET_COMPLIANCE_RULE,
        deleteMutationGQL: MUTATION_DELETE_COMPLIANCE_RULE,
        paginationLimit: props.dataPaginationLimit,
        pollInterval: props.pollInterval,
        updateQueryAfterFetchMore: (prev: QueryResult, fetchMoreResult: QueryResult) => {
            let result: QueryResult = {
                ...prev,
                CustomerComplianceRules: {
                    ...prev.CustomerComplianceRules,
                    docs: [
                        ...prev.CustomerComplianceRules.docs,
                        ...fetchMoreResult.CustomerComplianceRules.docs
                    ]
                }
            }
            return result;
        },
        transformEditFromExisting: (values) => {
            return {
                ...values,
                inEffectDate: momentOrNull(values.inEffectDate),
                expirationDate: momentOrNull(values.expirationDate),
                ticketTemplatesThatSatisfy: values.ticketTemplateIdsThatSatisfyDocs ? values.ticketTemplateIdsThatSatisfyDocs.map(t => ({
                    key: t._id,
                    label: t.onScreenName
                })) : []
            }
        }
    })
    const data = MDState.data;

    async function handleFieldsChange(_, allFields: ComplianceRuleEntryFormData){
        MDState.setEntryFields(allFields);
    }

    return <MDLayout
        {...MDStateToLayoutProps(MDState)}
        noDataElement={renderNoData(MDState, () => {
            return MDState.editFromNew({
                name: MDState.getSearchValue('onScreenNameStartsWith')
            })
        })}
        headerElement={<MDHeader
            {...MDStateToHeaderProps(MDState)}
            inputElement={<div style={{ display: 'flex', gap: '12px' }}>
                <Input.Search
                    onChange={(e) => MDState.onSearchValueChange('onScreenNameStartsWith', e.target.value)}
                    placeholder="Search by short name"
                    value={MDState.getSearchValue('onScreenNameStartsWith')}
                    style={{ width: '15rem' }}
                    allowClear
                />
            </div>}
        />}
        tableElement={<TableLoader mdState={MDState}>
            <BlueprintTableInfiniteScroller {...MDStateToTableScrollerProps(MDState)} >
                <MDTable
                    {...getMDTableProps(data, MDState)}
                    onDelete={(idx) => {
                        let entry = data[idx];
                        MDState.delete({ id: entry['_id'], tpID: orgData.getOrgIDByType('transporter') });
                    }}
                    columnWidths={[134, 134, 134, 134, 134]}
                >
                    <Column name="Name" cellRenderer={renderCellFromKeyIndex(data, 'onScreenName')} />
                    <Column name="Short Desc" cellRenderer={renderCellFromKeyIndex(data, 'shortDescription')} />
                    <Column name="In Effect Date" cellRenderer={(idx) => {
                        let record: ComplianceRuleFragmentFragment = data[idx];
                        return <Cell>{record?.inEffectDate ? momentOrNull(record.inEffectDate)?.format('YYYY-MM-DD') : 'N/A'}</Cell>
                    }} />
                    <Column name="Exp Date" cellRenderer={(idx) => {
                        let record: ComplianceRuleFragmentFragment = data[idx];
                        return <Cell>{record?.expirationDate ? momentOrNull(record.expirationDate)?.format('YYYY-MM-DD') : 'N/A'}</Cell>
                    }} />
                    <Column name="Active" cellRenderer={(idx) => {
                        let record: ComplianceRuleFragmentFragment = data[idx];
                        let active = record && record.isActive;
                        return <Cell intent={active ? 'success' : 'danger'}>{active ? 'Yes' : 'No'}</Cell>
                    }} />
                </MDTable>
            </BlueprintTableInfiniteScroller>
        </TableLoader>}
        detailsElement={<MDDetails
            {...MDStateToDetailsProps(MDState, 'onScreenName')}
            deleteUserGroups={DELETE_USER_GROUPS}
            onSave={() => {
                formRef.current.validateFieldsAndScroll((err, values) => {
                    if (!err){
                        const data = {
                            payload: {
                                ...values,
                                _id: MDState.getEntryFieldValue('_id'),
                                inEffectDate: momentOrNull(values.inEffectDate)?.format('YYYY-MM-DD'),
                                expirationDate: momentOrNull(values.expirationDate)?.format('YYYY-MM-DD'),
                                ticketTemplateIdsThatSatisfy: values.ticketTemplatesThatSatisfy
                                    ?.map((labelInValue) => labelInValue?.key)
                            }
                        }

                        // This is needed b/c GraphQL does not expect this field. It expects ticketTemplateIdsThatSatisfy.
                        delete data.payload.ticketTemplatesThatSatisfy;
                        MDState.save(data);
                    }
                })
            }}
            onDelete={
                () => {
                    let delData = {
                        id: MDState.getEntryFieldValue('_id'),
                        tpID: orgData.getOrgIDByType('transporter')
                    }
                    MDState.delete(delData)
                }
            }
        >
            <ComplianceRuleEntryForm
                formFields={MDState.entryFields}
                onFieldsChange={handleFieldsChange}
                uppercaseFields={['onScreenName']}
                autoFocus
                ref={formRef}
                isNewEntry={MDState.isNewEntry}
                dupeChecker={MDState.isNewEntry ? (
                    <DuplicationChecker
                        skip={
                            !trimString(MDState.getEntryFieldValue('onScreenNameStartsWith'))
                        }
                        getData={data => data && data.CustomerComplianceRules && data.CustomerComplianceRules.docs}
                        query={QUERY_CUSTOMER_COMPLIANCE_RULES}

                        // TODO: Implement duplication checker query
                        variables={baseQueryArgs}

                        renderItems={(items) => {
                            return <List size="small">
                                {items.map((rule: ComplianceRuleFragmentFragment, idx) =>
                                    <List.Item key={idx}>
                                        <Button
                                            className="mc-link-btn"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                MDState.editFromExisting(rule, false);
                                            }}
                                        >
                                            <strong>Name: </strong>{rule.onScreenName}
                                        </Button>
                                    </List.Item>)}
                            </List>
                        }}
                    />
                ) : null}
            >

            </ComplianceRuleEntryForm>
            <label style={{fontSize:'5px'}}>{MDState.getEntryFieldValue('_id')}</label>
        </MDDetails>
        }
    />
}

export default ComplianceRulesPage